import React, { useContext, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Stack,
  Typography,
} from '@mui/material';
import { UserContext } from './UserContext';
import {
  acceptOrganizationInvitation,
  getOrganizationInvitationDetails,
  getUser,
} from './apiService';
import { darkPanelSx, sectionCardSx } from './designSystem';
import { formatOrganizationRole } from './roleLabels';

function InviteAcceptPage() {
  const { token } = useParams();
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const loadDetails = async () => {
      try {
        setLoading(true);
        const response = await getOrganizationInvitationDetails(token);
        setDetails(response);
      } catch (error) {
        setErrorMessage(error.response?.data?.detail || 'Unable to load this invitation.');
      } finally {
        setLoading(false);
      }
    };

    loadDetails();
  }, [token]);

  const handleAccept = async () => {
    try {
      setAccepting(true);
      setErrorMessage('');
      await acceptOrganizationInvitation(token);
      const updatedUser = await getUser();
      setUser(updatedUser);
      setSuccessMessage('Invitation accepted. Redirecting to the school dashboard...');
      setTimeout(() => navigate('/'), 1200);
    } catch (error) {
      setErrorMessage(error.response?.data?.detail || 'Unable to accept this invitation.');
    } finally {
      setAccepting(false);
    }
  };

  const invite = details?.invitation;
  const organization = details?.organization;
  const inviteEmail = invite?.email || '';
  const signedInEmail = user?.email?.toLowerCase();
  const inviteMatchesSignedInUser = user && signedInEmail === inviteEmail.toLowerCase();

  return (
    <Container
      sx={{
        py: { xs: 5, md: 8 },
        minHeight: { md: 'calc(100vh - 220px)' },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ maxWidth: 980, mx: 'auto' }}>
        <Card sx={{ ...sectionCardSx, overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '0.9fr 1.1fr' } }}>
            <Box sx={{ ...darkPanelSx, p: { xs: 4, md: 5 }, borderRadius: 0, boxShadow: 'none' }}>
              <Chip label="School Invitation" sx={{ bgcolor: 'rgba(255,255,255,0.14)', color: 'white', fontWeight: 800 }} />
              <Typography variant="h3" sx={{ mt: 2.5 }}>
                Join a shared school workspace.
              </Typography>
              <Typography sx={{ mt: 1.5, color: '#d4e3e8', lineHeight: 1.8 }}>
                Use this page to join the BlockWebAI workspace your school or district created for policy management, reporting, and staff coordination.
              </Typography>
            </Box>
            <CardContent sx={{ p: { xs: 4, md: 6 } }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h4" sx={{ color: '#0B2E43' }}>
                  Invitation details
                </Typography>
                <Typography sx={{ mt: 1, color: '#52677A', lineHeight: 1.8 }}>
                  Confirm the email and role match the staff member who should join this workspace.
                </Typography>
              </Box>

              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
              {successMessage && <Alert severity="success">{successMessage}</Alert>}

              {loading ? (
                <Typography sx={{ color: '#52677A' }}>Loading invitation details...</Typography>
              ) : invite ? (
                <>
                  <Box sx={{ p: 3, borderRadius: '18px', bgcolor: 'rgba(11,46,67,0.04)', border: '1px solid rgba(11,46,67,0.08)' }}>
                    <Typography sx={{ color: '#52677A', fontWeight: 700 }}>Organization</Typography>
                    <Typography variant="h4" sx={{ color: '#0B2E43', mt: 0.75 }}>
                      {organization?.name || 'School workspace'}
                    </Typography>
                    <Typography sx={{ mt: 1, color: '#52677A' }}>
                      Role: {formatOrganizationRole(invite.role)} • Email: {invite.email}
                    </Typography>
                    <Typography sx={{ mt: 0.5, color: '#52677A' }}>
                      Status: {invite.status}
                    </Typography>
                  </Box>

                  {!user && (
                    <Alert severity="info">
                      Sign in or create an account with <strong>{invite.email}</strong> before accepting this invitation.
                    </Alert>
                  )}

                  {user && !inviteMatchesSignedInUser && (
                    <Alert severity="warning">
                      You are signed in as <strong>{user.email}</strong>, but this invitation was issued to <strong>{invite.email}</strong>.
                    </Alert>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    {!user ? (
                      <>
                        <Button variant="contained" component={RouterLink} to={`/login?invite=${token}`}>
                          Sign In to Accept
                        </Button>
                        <Button variant="outlined" component={RouterLink} to={`/register?invite=${token}`}>
                          Create Account for This Invite
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleAccept}
                        disabled={accepting || !inviteMatchesSignedInUser || invite.status !== 'pending'}
                      >
                        {accepting ? 'Accepting...' : 'Accept Invitation'}
                      </Button>
                    )}
                    <Button variant="text" component={RouterLink} to="/">
                      Back to Home
                    </Button>
                  </Stack>
                </>
              ) : null}
            </Stack>
            </CardContent>
          </Box>
        </Card>
      </Box>
    </Container>
  );
}

export default InviteAcceptPage;
